import React, { useState } from "react";
import { toast } from "react-toastify";
import ButtonSecondary from "../../buttons/ButtonSecondary";
import useMessagesApi from "../../../hooks/api/useMessagesApi";

const MessageListItem = ({ messages, isSelected, onClick, onDelete }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteMessage } = useMessagesApi();

  const { id, subject, body, created_at, file_paths, filePaths } = messages;
  
  const hasAttachments = () => {
    // Check file_paths array for valid paths
    const paths = file_paths || filePaths;
    if (!paths) {
      return false;
    }
    
    if (Array.isArray(paths)) {
      // Check if there's at least one path that is not empty
      return paths.some(item => {
        if (typeof item === 'string') {
          return item.trim().length > 0;
        }
        if (typeof item === 'object' && item !== null) {
          const path = item.path || '';
          return path.trim().length > 0;
        }
        return false;
      });
    }
    
    if (typeof paths === 'string') {
      return paths.trim().length > 0;
    }
    
    return false;
  };

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      const response = await deleteMessage(id);
      if (response.status === 200) {
        toast.success('Message deleted successfully');
        if (onDelete) onDelete(messages);
      }
    } catch (error) {
      console.error("Failed to delete message:", error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to delete message';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  const getPreview = (text) => {
    if (!text) return "";
    const plain = text.replace(/<[^>]+>/g, "");
    return plain.length > 40 ? plain.slice(0, 40) + "..." : plain;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date
    
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    return `${month} ${day} ${year}`;
  };

  return (
    <>
      <div
        onClick={() => onClick(messages)}
        className={`
          flex items-start gap-3 p-4 cursor-pointer
          transition-colors duration-200 rounded-lg
          ${isSelected ? "bg-green-50" : "hover:bg-gray-50"}
        `}
      >
        {/* Avatar Circle */}
        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
          <span className="text-white text-bodyMM font-semibold">
            {getInitials(subject || "NA")}
          </span>
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-dark2 text-bodyMM font-normal truncate">
                {subject}
              </h4> 
              <p className="text-dark5 text-bodyESM mt-0 truncate">
                {getPreview(body)}
              </p>
              <p className="text-dark5 text-bodyESM mt-0 truncate">
                {formatDate(created_at)}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* File Attachment Icon */}
              {hasAttachments() && (
                <div className="text-dark4" title="Has attachments">
                  <img 
                    src={`${process.env.PUBLIC_URL}/file-icon.svg`} 
                    alt="attachment" 
                    className="w-4 h-4"
                  />
                </div>
              )}
              
              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteModalOpen(true);
                }}
                className="rounded hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors duration-200"
                title="Delete message"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                  <path
                    fillRule="evenodd"
                    d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🧱 Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div
          className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50"
          onClick={() => setDeleteModalOpen(false)}
        >
          <div
            className="bg-white p-8 rounded-lg mx-6 max-w-[500px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-h6 text-dark4 mb-4 text-center">
              Are you sure you want to delete this message?
            </h2>
            <p className="text-bodyMR text-dark4 text-center mb-2">
              This action is irreversible.
            </p>
            <div className="flex justify-center mt-6 space-x-4">
              <ButtonSecondary
                text={isDeleting ? "Deleting..." : "Delete"}
                customClass="bg-redDark text-white text-bodyMR px-[22px] py-[10px]"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              />
              <ButtonSecondary
                text="Cancel"
                customClass="bg-white text-dark4 text-bodyMR border border-dark4 px-[22px] py-[10px]"
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageListItem;
